import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Storage } from "aws-amplify";
import {
  RiUploadCloud2Line,
  RiCheckLine,
  RiRefreshLine,
  RiDeleteBin5Line,
  RiAlertLine,
  RiImage2Line,
  RiLoader4Line,
  RiCloseLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from "react-icons/ri";
import ModalConfirm from "../../components/ModalConfirm";
import { MediaUri } from "../../constants/enum";
import { markPackageCaseUpdated } from "../../apis/product";
import { MAX_FILE_SIZE } from "./PhotoDropZone";
import { humanFileSize } from "./format";

type UploadStatus = "pending" | "uploading" | "success" | "failed";

interface PendingFile {
  id: string;
  file: File;
  previewUrl: string;
  status: UploadStatus;
  progress: number;
  error?: string;
}

// One image already stored for the matcode (discovered by listing S3 thumbnails).
interface ExistingPhoto {
  path: string; // "<matcode>/<file>"
  name: string; // file name only
  url: string; // productlibrary-media thumbnail URL
}

// Lists every stored photo for a matcode from files/thumbnail/<matcode>/.
async function listExistingPhotos(matcode: string): Promise<ExistingPhoto[]> {
  const res: any = await Storage.list(`files/thumbnail/${matcode}/`, {
    level: "public",
    pageSize: 100,
  });
  return (res.results ?? [])
    .filter(
      (o: any) => o.key && o.size > 0 && /\.(jpe?g|png)$/i.test(o.key)
    )
    .map((o: any): ExistingPhoto => {
      const path = o.key.replace("files/thumbnail/", "");
      return {
        path,
        name: path.split("/").pop() ?? path,
        url: `${MediaUri}/thumbnail/${path}`,
      };
    });
}

// A logical photo lives across several S3 prefixes (original + Lambda-generated
// display/thumbnail/downloads). Remove them all; missing derived copies 404 and
// are ignored. path === "<matcode>/<file>".
async function deletePhotoEverywhere(path: string): Promise<void> {
  const keys = [
    `original/${path}`,
    `files/display/${path}`,
    `files/thumbnail/${path}`,
    `downloads/large/${path}`,
    `downloads/medium/${path}`,
    `downloads/small/${path}`,
  ];
  await Promise.all(
    keys.map((k) =>
      Storage.remove(k, { level: "public" }).catch(() => undefined)
    )
  );
}

// Same constraints the drag-and-drop UploadTab enforces, applied to the direct
// per-matcode file picker (which can't use react-dropzone's validators).
function rejectFile(file: File): string | null {
  if (file.type !== "image/jpeg" && file.type !== "image/png")
    return "ชนิดไฟล์ไม่รองรับ (รับเฉพาะ .jpg .jpeg .png)";
  if (file.size > MAX_FILE_SIZE) return "ไฟล์มีขนาดใหญ่เกิน 20 MB";
  if (/\s/.test(file.name))
    return "ชื่อไฟล์ต้องไม่มีช่องว่าง (space)";
  if (!/^[a-zA-Z0-9._-]+$/.test(file.name))
    return "ชื่อไฟล์ต้องเป็นภาษาอังกฤษเท่านั้น";
  return null;
}

// Uploads one or more images straight into original/<matcode>/ for a single,
// known material code (no folder-name parsing, no account/channel).
function MatcodeUploadModal({
  matcode,
  onClose,
  onUploaded,
}: {
  matcode: string | null;
  onClose: () => void;
  onUploaded?: (matcode: string) => void;
}) {
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [rejected, setRejected] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Existing (already-uploaded) photos for this matcode.
  const [existing, setExisting] = useState<ExistingPhoto[]>([]);
  const [existingLoading, setExistingLoading] = useState(false);
  const [confirmPath, setConfirmPath] = useState<string | null>(null);
  const [deletingPath, setDeletingPath] = useState<string | null>(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  // Lightbox: index of the enlarged photo, or null when closed.
  const [viewIndex, setViewIndex] = useState<number | null>(null);

  // Close the lightbox if its photo disappears (deleted / list changed).
  useEffect(() => {
    setViewIndex((i) =>
      i === null ? null : i < existing.length ? i : null
    );
  }, [existing]);

  const refreshExisting = useCallback(async () => {
    if (!matcode) return;
    setExistingLoading(true);
    try {
      setExisting(await listExistingPhotos(matcode));
    } catch (err) {
      console.log("list existing photos error:", err);
      setExisting([]);
    } finally {
      setExistingLoading(false);
    }
  }, [matcode]);

  // Reset everything whenever the modal targets a different matcode (or closes),
  // then load that matcode's existing photos.
  const filesRef = useRef<PendingFile[]>(files);
  filesRef.current = files;
  useEffect(() => {
    filesRef.current.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setFiles([]);
    setRejected([]);
    setIsUploading(false);
    setConfirmPath(null);
    setDeletingPath(null);
    setConfirmDeleteAll(false);
    setDeletingAll(false);
    setDragActive(false);
    setViewIndex(null);
    setExisting([]);
    if (matcode) refreshExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matcode]);

  const handleDeleteAll = async () => {
    if (existing.length === 0) return;
    setDeletingAll(true);
    try {
      await Promise.all(existing.map((p) => deletePhotoEverywhere(p.path)));
      setExisting([]);
      await markPackageCaseUpdated(matcode!);
      onUploaded?.(matcode!);
    } catch (err) {
      console.log("delete all photos error:", err);
    } finally {
      setDeletingAll(false);
      setConfirmDeleteAll(false);
    }
  };

  const handleDelete = async (photo: ExistingPhoto) => {
    setDeletingPath(photo.path);
    try {
      await deletePhotoEverywhere(photo.path);
      setExisting((prev) => prev.filter((p) => p.path !== photo.path));
      await markPackageCaseUpdated(matcode!);
      onUploaded?.(matcode!); // notify parent so the table refetches
    } catch (err) {
      console.log("delete photo error:", err);
    } finally {
      setDeletingPath(null);
      setConfirmPath(null);
    }
  };

  // Shared by the file picker and drag-and-drop: validate then queue.
  const addFiles = (picked: File[]) => {
    const additions: PendingFile[] = [];
    const newRejected: string[] = [];
    for (const file of picked) {
      const reason = rejectFile(file);
      if (reason) {
        newRejected.push(`${file.name} — ${reason}`);
        continue;
      }
      additions.push({
        id: `${file.name}_${file.size}`,
        file,
        previewUrl: URL.createObjectURL(file),
        status: "pending",
        progress: 0,
      });
    }
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.id));
      return [...prev, ...additions.filter((a) => !existing.has(a.id))];
    });
    if (newRejected.length) setRejected((prev) => [...prev, ...newRejected]);
  };

  const onFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-picking the same file
    addFiles(picked);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (isUploading) return;
    addFiles(Array.from(e.dataTransfer.files ?? []));
  };

  const patch = (id: string, p: Partial<PendingFile>) =>
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...p } : f)));

  const uploadOne = async (item: PendingFile): Promise<boolean> => {
    if (!matcode) return false;
    patch(item.id, { status: "uploading", progress: 0, error: undefined });
    try {
      await Storage.put(`original/${matcode}/${item.file.name}`, item.file, {
        level: "public",
        contentType: item.file.type,
        progressCallback: (prog: { loaded: number; total: number }) => {
          const pct = prog.total
            ? Math.round((prog.loaded / prog.total) * 100)
            : 0;
          patch(item.id, { progress: pct });
        },
      });
      patch(item.id, { status: "success", progress: 100 });
      return true;
    } catch (err: any) {
      patch(item.id, {
        status: "failed",
        error: err?.message ?? "อัปโหลดไม่สำเร็จ",
      });
      return false;
    }
  };

  const handleUpload = async () => {
    if (!matcode) return;
    setIsUploading(true);
    let anyOk = false;
    for (const item of files.filter((f) => f.status !== "success")) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await uploadOne(item);
      anyOk = anyOk || ok;
    }
    if (anyOk) {
      // Stamp first (bumps updatedAt) so the table refetch reflects the change.
      await markPackageCaseUpdated(matcode);
      onUploaded?.(matcode);
      // Thumbnails are generated asynchronously by the resize Lambda — give it a
      // moment, then refresh so the just-uploaded photos appear under "รูปเดิม".
      setTimeout(() => {
        refreshExisting();
      }, 2500);
    }
    setIsUploading(false);
  };

  const removeFile = (id: string) =>
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.id !== id);
    });

  const pendingCount = files.filter((f) => f.status !== "success").length;
  const successCount = files.filter((f) => f.status === "success").length;

  return (
    <ModalConfirm
      show={!!matcode}
      onHide={onClose}
      title={`จัดการรูป · ${matcode ?? ""}`}
      showCloseIcon={!isUploading}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-4">
        {/* Existing photos (view + delete). Re-upload the same filename = update. */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">
              รูปเดิม{" "}
              <span className="font-normal text-gray-400">
                ({existing.length})
              </span>
            </span>
            <div className="flex items-center gap-3">
              {existing.length > 0 && !confirmDeleteAll && (
                <button
                  type="button"
                  onClick={() => setConfirmDeleteAll(true)}
                  disabled={deletingAll || !!deletingPath}
                  title="ลบรูปทั้งหมดของรหัสนี้"
                  className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-600 disabled:opacity-50"
                >
                  <RiDeleteBin5Line size={14} />
                  ลบทั้งหมด
                </button>
              )}
              <button
                type="button"
                onClick={refreshExisting}
                disabled={existingLoading || deletingAll}
                title="โหลดรูปเดิมใหม่"
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                <RiRefreshLine
                  size={14}
                  className={existingLoading ? "animate-spin" : ""}
                />
                รีเฟรช
              </button>
            </div>
          </div>

          {/* Delete-all confirm bar */}
          {confirmDeleteAll && (
            <div className="flex items-center justify-between gap-2 p-2 mb-2 border rounded-lg border-red-200 bg-red-50">
              <span className="flex items-center gap-1.5 text-xs font-medium text-red-700">
                <RiAlertLine size={14} />
                {deletingAll
                  ? "กำลังลบทั้งหมด…"
                  : `ลบรูปทั้งหมด ${existing.length} รูป? กู้คืนไม่ได้`}
              </span>
              {!deletingAll && (
                <div className="flex gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={handleDeleteAll}
                    className="rounded bg-red-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-600"
                  >
                    ลบทั้งหมด
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteAll(false)}
                    className="rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    ยกเลิก
                  </button>
                </div>
              )}
            </div>
          )}

          {existingLoading && existing.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-400">
              <RiLoader4Line size={16} className="animate-spin" /> กำลังโหลด…
            </div>
          ) : existing.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-center border border-gray-200 border-dashed rounded-lg">
              <RiImage2Line size={26} className="mb-1 text-gray-300" />
              <p className="text-xs text-gray-400">ยังไม่มีรูปสำหรับรหัสนี้</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
              {existing.map((photo, idx) => {
                const isConfirming = confirmPath === photo.path;
                const isDeleting = deletingPath === photo.path;
                return (
                  <div
                    key={photo.path}
                    className="relative overflow-hidden border border-gray-200 rounded-lg group aspect-square bg-gray-50"
                  >
                    {/* Click image to enlarge */}
                    <button
                      type="button"
                      onClick={() => setViewIndex(idx)}
                      title="กดเพื่อดูรูปใหญ่"
                      className="block w-full h-full cursor-zoom-in"
                    >
                      <img
                        src={photo.url}
                        alt={photo.name}
                        loading="lazy"
                        className="object-cover w-full h-full"
                      />
                    </button>
                    {/* Delete affordance */}
                    {!isConfirming && !isDeleting && (
                      <button
                        type="button"
                        onClick={() => setConfirmPath(photo.path)}
                        title="ลบรูปนี้"
                        className="absolute p-1 text-white transition-opacity rounded-full opacity-0 top-1 right-1 bg-black/50 group-hover:opacity-100 hover:bg-red-600"
                      >
                        <RiDeleteBin5Line size={14} />
                      </button>
                    )}
                    {/* Inline confirm */}
                    {isConfirming && !isDeleting && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-1 text-center bg-black/60">
                        <span className="text-[11px] font-medium text-white">
                          ลบรูปนี้?
                        </span>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => handleDelete(photo)}
                            className="rounded bg-red-500 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-red-600"
                          >
                            ลบ
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmPath(null)}
                            className="rounded bg-white/90 px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-white"
                          >
                            ยกเลิก
                          </button>
                        </div>
                      </div>
                    )}
                    {isDeleting && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <RiLoader4Line
                          size={20}
                          className="text-white animate-spin"
                        />
                      </div>
                    )}
                    <span className="absolute inset-x-0 bottom-0 px-1 py-0.5 text-[10px] text-white truncate bg-black/40 pointer-events-none">
                      {photo.name}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Picker + drag-and-drop */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => !isUploading && inputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !isUploading)
              inputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!isUploading) setDragActive(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragActive(false);
          }}
          onDrop={onDrop}
          className={`flex items-center justify-center gap-2 w-full px-4 py-3 text-center border-2 border-dashed rounded-xl transition-colors ${
            isUploading
              ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-60"
              : dragActive
              ? "cursor-pointer border-blue-500 bg-blue-50"
              : "cursor-pointer border-gray-300 bg-gray-50/60 hover:border-blue-400 hover:bg-blue-50/50"
          }`}
        >
          <RiUploadCloud2Line
            size={20}
            className={dragActive ? "text-blue-600" : "text-blue-500"}
          />
          <span className="text-sm font-medium text-gray-700">
            {dragActive ? "วางรูปที่นี่" : "ลากรูปมาวาง หรือคลิกเพื่อเลือก"}
          </span>
          <span className="hidden text-[11px] text-gray-400 sm:inline">
            · .jpg .jpeg .png ≤20MB · ชื่อไฟล์เดิม = อัปเดต
          </span>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,.jpg,.jpeg,.png"
          multiple
          className="hidden"
          onChange={onFilesSelected}
        />

        {/* File list */}
        {files.length > 0 && (
          <ul className="border divide-y divide-gray-100 rounded-lg border-gray-200 max-h-64 overflow-y-auto">
            {files.map((f) => (
              <li key={f.id} className="flex items-center gap-3 px-3 py-2">
                <img
                  src={f.previewUrl}
                  alt={f.file.name}
                  className="object-cover w-10 h-10 border border-gray-200 rounded-md bg-gray-50 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-gray-700 truncate">
                      {f.file.name}
                    </span>
                    <span className="text-xs text-gray-400 shrink-0">
                      {humanFileSize(f.file.size)}
                    </span>
                  </div>
                  {(f.status === "uploading" || f.status === "success") && (
                    <div className="w-full h-1.5 mt-1.5 overflow-hidden bg-gray-100 rounded-full">
                      <div
                        className={`h-full rounded-full transition-all duration-200 ${
                          f.status === "success" ? "bg-green-500" : "bg-blue-500"
                        }`}
                        style={{ width: `${f.progress}%` }}
                      />
                    </div>
                  )}
                  {f.status === "failed" && f.error && (
                    <p className="mt-0.5 text-xs text-red-500 truncate">
                      {f.error}
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-end w-7 shrink-0">
                  {f.status === "success" && (
                    <span className="flex items-center justify-center w-6 h-6 text-green-600 rounded-full bg-green-50">
                      <RiCheckLine size={15} />
                    </span>
                  )}
                  {f.status === "uploading" && (
                    <span className="text-xs font-medium text-blue-500">
                      {f.progress}%
                    </span>
                  )}
                  {f.status === "failed" && (
                    <button
                      type="button"
                      onClick={() => uploadOne(f)}
                      disabled={isUploading}
                      className="flex items-center justify-center w-6 h-6 text-red-500 rounded-full bg-red-50 hover:bg-red-100 disabled:opacity-40"
                      title="ลองใหม่"
                    >
                      <RiRefreshLine size={14} />
                    </button>
                  )}
                  {f.status === "pending" && !isUploading && (
                    <button
                      type="button"
                      onClick={() => removeFile(f.id)}
                      className="flex items-center justify-center w-6 h-6 text-gray-400 rounded-full hover:bg-gray-100 hover:text-red-500"
                      title="ลบ"
                    >
                      <RiDeleteBin5Line size={14} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Rejected files */}
        {rejected.length > 0 && (
          <div className="p-3 border rounded-lg border-amber-200 bg-amber-50">
            <p className="flex items-center gap-1.5 mb-1 text-sm font-semibold text-amber-800">
              <RiAlertLine size={16} /> ข้าม {rejected.length} ไฟล์
            </p>
            <ul className="space-y-0.5 text-xs text-amber-700">
              {rejected.map((r, i) => (
                <li key={`${r}_${i}`} className="truncate">
                  {r}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-gray-500">
            {successCount > 0
              ? `อัปโหลดแล้ว ${successCount}/${files.length}`
              : files.length > 0
              ? `พร้อมอัปโหลด ${files.length} ไฟล์`
              : ""}
          </span>
          <button
            type="button"
            onClick={handleUpload}
            disabled={isUploading || pendingCount === 0}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isUploading ? (
              "กำลังอัปโหลด…"
            ) : (
              <>
                <RiUploadCloud2Line size={16} /> อัปโหลด {pendingCount} ไฟล์
              </>
            )}
          </button>
        </div>

        {successCount > 0 && !isUploading && (
          <p className="flex items-center gap-1.5 text-xs text-green-600">
            <RiCheckLine size={14} /> อัปโหลดสำเร็จ {successCount} ไฟล์
          </p>
        )}

        {/* Lightbox — enlarged view of a grid photo. Portaled to <body> so it is
            truly viewport-fixed (the modal panel's transform would otherwise trap
            a fixed child inside the small panel box). */}
        {viewIndex !== null &&
          existing[viewIndex] &&
          createPortal(
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => setViewIndex(null)}
            >
            {/* Close */}
            <button
              type="button"
              onClick={() => setViewIndex(null)}
              className="absolute p-2 text-white rounded-full top-4 right-4 hover:bg-white/10"
              title="ปิด"
            >
              <RiCloseLine size={26} />
            </button>
            {/* Prev */}
            {existing.length > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setViewIndex(
                    (i) => ((i as number) - 1 + existing.length) % existing.length
                  );
                }}
                className="absolute p-2 text-white -translate-y-1/2 rounded-full left-4 top-1/2 hover:bg-white/10"
                title="ก่อนหน้า"
              >
                <RiArrowLeftSLine size={30} />
              </button>
            )}
            <figure
              className="flex flex-col items-center max-w-3xl max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={`${MediaUri}/display/${existing[viewIndex].path}`}
                alt={existing[viewIndex].name}
                className="object-contain max-h-[80vh] max-w-full rounded-lg"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src =
                    existing[viewIndex].url;
                }}
              />
              <figcaption className="mt-2 text-sm text-white/80">
                {existing[viewIndex].name} ({viewIndex + 1}/{existing.length})
              </figcaption>
            </figure>
            {/* Next */}
            {existing.length > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setViewIndex((i) => ((i as number) + 1) % existing.length);
                }}
                className="absolute p-2 text-white -translate-y-1/2 rounded-full right-4 top-1/2 hover:bg-white/10"
                title="ถัดไป"
              >
                <RiArrowRightSLine size={30} />
              </button>
            )}
            </div>,
            document.body
          )}
      </div>
    </ModalConfirm>
  );
}

export default MatcodeUploadModal;
